/**
 * The console's one HTTP client. Same-origin `fetch` with the session cookie
 * (`credentials: 'same-origin'`; the cookie is HttpOnly and set by
 * `/app/auth/:token`), the CSRF token on every non-GET (src/console/auth.ts
 * refuses mutations without it), and RFC 7807 problems turned into
 * `ApiError`s the screens can read (`problem.type`, `detail`, extras such as
 * `candidates`). A 401 anywhere means the session is gone: the session
 * store is told, and the router shows the sign-in screen.
 */
import { useSessionStore } from '../store/session.js';

export interface Problem {
  type: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  [k: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem | null;
  constructor(status: number, problem: Problem | null, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
  }
  /** `/problems/<slug>` → `<slug>`; `about:blank` → null. */
  get slug(): string | null {
    const t = this.problem?.type;
    if (!t || !t.startsWith('/problems/')) return null;
    return t.slice('/problems/'.length);
  }
}

export function isProblem(v: unknown): v is Problem {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as Problem).status === 'number' &&
    typeof (v as Problem).title === 'string'
  );
}

export interface RequestInit {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = init.method ?? 'GET';
  const headers: Record<string, string> = { accept: 'application/json' };
  if (init.body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET') {
    const csrf = useSessionStore.getState().csrfToken;
    if (csrf) headers['x-csrf-token'] = csrf;
  }
  const res = await fetch(path, {
    method,
    headers,
    credentials: 'same-origin',
    ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
    ...(init.signal ? { signal: init.signal } : {}),
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let json: unknown = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }
  if (!res.ok) {
    if (res.status === 401) useSessionStore.getState().signOut();
    const problem = isProblem(json) ? json : null;
    throw new ApiError(
      res.status,
      problem,
      problem?.detail ?? problem?.title ?? `HTTP ${res.status}`,
    );
  }
  return json as T;
}

/** Human line for an error the user can act on. */
export function describeError(err: unknown): string {
  if (err instanceof ApiError) return err.problem?.detail ?? err.message;
  if (err instanceof Error) return err.message;
  return 'Something went wrong.';
}
